/**
 * AI-mail — /ai-mail (okt 2026, docs/ai-mail-plan.md).
 *
 * Joris markeert een mail in Outlook met de categorie "AI" (ook op de telefoon), of stuurt hem door naar zichzelf
 * met "AI:" vooraan. Toolframe maakt per mail een voorstel + conceptantwoord. Na Goedkeuren (5 s terughaalbaar)
 * komt het concept in Concepten en de taak in To Do, en wordt de categorie "AI ✓". Nooit versturen.
 * Afwijzen = "AI ✗" (met Ongedaan maken). Een open vraag = "AI ?". Zelf afgehandeld (knop, of automatisch als jij of
 * een collega al antwoordde) = "AI ✓", zonder concept of taak.
 *
 * API: /Api/ai-mail — main via Node (backend/routes/aiMail.js), develop via de engine-voordeur
 * (api/frontdoor/ai_mail.py). Rustig ontwerp: één kolom, één accentkleur, details klein en grijs.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, Link, Radio, RadioGroup, Stack, Switch, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { bevestig } from '../../utils/bevestig';
import {
  aiMailApi, euro, gesprekTekst, grippSchrijft, kanZelfAfhandelen, mailActie, meldingTekst, MODUS_TEKST, MODUS_UITLEG,
  ontvangersTekst, STATUS_TEKST, watGebeurt, wijzigingen,
} from './aiMailLogica';

const KLEUR = {
  tekst: '#1f2937', zacht: '#6b7280', lijn: '#eceef1', accent: '#526075', accentZacht: '#eef2f8',
  vraag: '#9a3412', vraagZacht: '#fff7ed', fout: '#b42318',
};

function Label({ children, kleur = KLEUR.zacht }) {
  return <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: kleur, letterSpacing: '0.02em' }}>{children}</Typography>;
}

const ROUTE_TEKST = {
  relatie_wijzigen: 'Relatie op de factuur aanpassen en de factuur opnieuw versturen. Geen creditnota.',
  aanpassen_opnieuw: 'Factuur aanpassen en opnieuw versturen. Geen creditnota.',
  crediteren_opnieuw: 'Crediteren en opnieuw maken. Toolframe zet niets in Gripp; dit is de stap voor jou of finance.',
};

function FactuurVoorstel({ v, item }) {
  const f = (v.extra || {}).factuur || {};
  const waarom = f.route_bron === 'instructie' ? 'jouw opdracht' : f.route_reden;
  if (!f.route && !f.vraag) return null;
  return (
    <Stack spacing={0.75}>
      {f.factuur && (
        <Typography sx={{ fontSize: 14, color: KLEUR.tekst }}>
          Factuur {f.factuur.number} · {f.factuur.project_name || 'project onbekend'} · {euro(f.factuur.total_amount_excl_vat)} excl.
          {' '}· nu op <b>{f.huidige_relatie}</b>{f.nieuwe_relatie ? <> → <b>{f.nieuwe_relatie}</b></> : null}
        </Typography>
      )}
      {ROUTE_TEKST[f.route] && (
        <Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>
          {ROUTE_TEKST[f.route]}{waarom ? ` (${waarom})` : ''}
        </Typography>
      )}
      {f.route === 'relatie_wijzigen' && f.nieuwe_relatie && f.adres && (
        <Typography sx={{ fontSize: 14, color: KLEUR.tekst }}>{f.nieuwe_relatie}, {f.adres}</Typography>
      )}
      {(f.stappen || []).length > 0 && (
        <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: 14, color: KLEUR.tekst, '& li': { mb: 0.25 } }}>
          {f.stappen.map((s) => <li key={s}>{s}</li>)}
        </Box>
      )}
      {f.gripp_actie && (
        <Typography sx={{ fontSize: 12.5, color: KLEUR.zacht }}>
          {item.gripp_status === 'klaar' ? 'De relatie is in Gripp al aangepast.'
            : grippSchrijft(item) ? 'Bij goedkeuren past Toolframe de relatie in Gripp aan en leest de factuur daarna terug. Versturen doe je zelf.'
              : 'Toolframe past Gripp (nog) niet zelf aan: doe het via de link hieronder.'}
        </Typography>
      )}
      {f.gripp_url && <Link href={f.gripp_url} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>Open factuur in Gripp</Link>}
    </Stack>
  );
}

function Doorverwijzing({ item }) {
  const navigate = useNavigate();
  const v = item.voorstel || {};
  if (item.soort === 'offerte') {
    return (
      <Button size="small" variant="text" sx={{ textTransform: 'none', alignSelf: 'flex-start', px: 0 }}
        onClick={() => navigate('/offertes', { state: {
          sourceText: `E-mail van ${item.van_naam || ''} <${item.van_email || ''}> — ${item.onderwerp || ''}\n\n${item.tekst || ''}`,
          sourceLabel: item.onderwerp || 'Mail',
        } })}>
        Open in offertegenerator →
      </Button>
    );
  }
  if (item.soort === 'planning') {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        {(v.datums || []).length > 0 && <Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>Genoemd: {v.datums.join(', ')}</Typography>}
        <Button size="small" variant="text" sx={{ textTransform: 'none', px: 0 }} onClick={() => navigate('/draaidag-planner')}>
          Open draaidag-planner →
        </Button>
      </Stack>
    );
  }
  if (item.soort === 'po' && ((v.extra || {}).po || {}).kandidaten?.length) {
    return (
      <Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>
        PO in de mail: {v.extra.po.kandidaten.join(', ')}. De PO-check zelf staat in Admin → Algemene mailboxen.
      </Typography>
    );
  }
  return null;
}

function ItemKaart({ item, modus, onKlaar, focus = false, actie = null, onActieGezien = () => {} }) {
  const [concept, setConcept] = useState(item.concept_tekst || '');
  const [titel, setTitel] = useState(item.taak_titel || '');
  const [geenConcept, setGeenConcept] = useState(!(item.concept_tekst || '').trim());
  const [geenTaak, setGeenTaak] = useState(false);
  const [geenGripp, setGeenGripp] = useState(false);
  // Nieuw voorstel voor dezelfde mail (opnieuw geanalyseerd): toon dat, niet je oude lokale tekst.
  useEffect(() => {
    setConcept(item.concept_tekst || '');
    setTitel(item.taak_titel || '');
    setGeenConcept(!(item.concept_tekst || '').trim());
    setGeenGripp(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.versie]);
  const [origineel, setOrigineel] = useState('');
  const [bezig, setBezig] = useState(false);
  const v = item.voorstel || {};
  const kandidaten = (item.ontvangers || {}).kandidaten;
  const onzeker = item.concept_status === 'bezig' || item.todo_status === 'bezig';
  const moetKiezen = Array.isArray(kandidaten) && !geenConcept && !origineel;
  const kanBeslissen = ['voorstel', 'vraag'].includes(item.status);

  const goedkeuren = async (opnieuw = false) => {
    const body = wijzigingen(item, { concept_tekst: concept, taak_titel: titel, geen_concept: geenConcept, geen_taak: geenTaak, geen_gripp: geenGripp, origineel_id: origineel });
    setBezig(true);
    const tekst = modus === 'on' ? 'Wordt uitgevoerd…' : 'Proefbesluit wordt vastgelegd…';
    const r = await voerUitgesteldUit(tekst, () => aiMailApi.goedkeuren(item.id, body, opnieuw), { duur: 5000 });
    setBezig(false);
    if (r.geannuleerd) return;
    if (r.fout) { onKlaar(); return; }   // server kan intussen 'vraag' hebben gezet (bv. onderbroken stap)
    const d = r.resultaat || {};
    if (d.proef) toast.success('Vastgelegd als proef: niets in Outlook gezet');
    else if (d.ok) toast.success('Klaar: concept en taak staan klaar');
    else if (d.gripp) toast.error(`Gripp niet aangepast: ${d.reden || d.gripp}`);
    else if (d.onzeker || d.ontvangers_gewijzigd) toast('Er is een vraag; kijk bij de mail', { icon: '❓' });
    onKlaar();
  };

  const afwijzen = async () => {
    setBezig(true);
    try {
      await aiMailApi.afwijzen(item.id);
      biedOngedaanAan('Afgewezen', async () => { await aiMailApi.heropen(item.id); onKlaar(); });
      onKlaar();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const zelfAfgehandeld = async () => {
    setBezig(true);
    try {
      await aiMailApi.zelfAfgehandeld(item.id);
      biedOngedaanAan('Zelf afgehandeld', async () => { await aiMailApi.heropen(item.id); onKlaar(); });
      onKlaar();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  // Vanuit de melding-mail (?id=…&actie=…): eerst bevestigen, de link zelf doet nooit iets.
  const kaartRef = useRef(null);
  const actieGedaan = useRef(false);
  useEffect(() => {
    if (!focus) return;
    if (kaartRef.current?.scrollIntoView) kaartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (!actie || actieGedaan.current) return;
    actieGedaan.current = true;
    onActieGezien();
    const magGoedkeuren = modus !== 'off' && kanBeslissen && !moetKiezen && !(geenTaak && geenConcept);
    if (actie === 'goedkeuren' && !magGoedkeuren) {
      toast('Deze mail heeft eerst je keuze nodig; kijk hieronder', { icon: '❓' });
      return;
    }
    if (actie === 'afwijzen' && (modus === 'off' || !kanBeslissen)) return;
    if (actie === 'zelf-afgehandeld' && (modus === 'off' || !kanZelfAfhandelen(item))) return;
    const vragen = {
      goedkeuren: { titel: 'Goedkeuren?', tekst: `${item.onderwerp || 'Deze mail'}: ${watGebeurt(item, { geenConcept, geenTaak, geenGripp })}. Er wordt nooit iets verstuurd.`, knop: 'Goedkeuren' },
      afwijzen: { titel: 'Afwijzen?', tekst: `${item.onderwerp || 'Deze mail'} krijgt in Outlook de categorie AI ✗.`, knop: 'Afwijzen' },
      'zelf-afgehandeld': { titel: 'Zelf afgehandeld?', tekst: `${item.onderwerp || 'Deze mail'}: geen concept en geen taak; in Outlook wordt de categorie AI ✓.`, knop: 'Zelf afgehandeld' },
    };
    (async () => {
      const ok = await bevestig(vragen[actie]);
      if (!ok) return;
      if (actie === 'goedkeuren') await goedkeuren(false);
      else if (actie === 'afwijzen') await afwijzen();
      else await zelfAfgehandeld();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, actie]);

  return (
    <Box ref={kaartRef} sx={{ py: 3, borderBottom: `1px solid ${KLEUR.lijn}`, ...(focus ? { bgcolor: KLEUR.accentZacht, mx: -2, px: 2, borderRadius: 1 } : {}) }}
      data-testid={`ai-mail-${item.id}`}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
          <Label kleur={KLEUR.accent}>{item.soort_label}</Label>
          <Typography sx={{ fontWeight: 600, color: KLEUR.tekst }}>{item.van_naam || item.van_email}</Typography>
          <Typography sx={{ color: KLEUR.zacht, fontSize: 14, flex: 1, minWidth: 200 }}>
            {item.onderwerp}
            {gesprekTekst(item) && <Box component="span" sx={{ fontSize: 13 }}> · {gesprekTekst(item)}</Box>}
          </Typography>
          {item.status !== 'voorstel' && <Label kleur={item.status === 'vraag' ? KLEUR.vraag : KLEUR.zacht}>{STATUS_TEKST[item.status]}</Label>}
          {item.proef_besluit && <Label>Proef: {item.proef_besluit}</Label>}
          {item.web_link && <Link href={item.web_link} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>Open in Outlook</Link>}
        </Stack>

        {item.instructie && (
          <Typography sx={{ fontSize: 14, color: KLEUR.tekst }}><Label>Jouw opdracht</Label>&nbsp; {item.instructie}</Typography>
        )}
        {v.samenvatting && <Typography sx={{ fontSize: 15, color: KLEUR.tekst }}>{v.samenvatting}</Typography>}
        {item.vraag && (
          <Box sx={{ bgcolor: KLEUR.vraagZacht, color: KLEUR.vraag, px: 1.5, py: 1, borderRadius: 1, fontSize: 14 }}>
            {item.vraag}
          </Box>
        )}

        {item.soort === 'factuur_aanpassen' && <FactuurVoorstel v={v} item={item} />}
        {((v.extra || {}).project) && (
          <Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>
            Project {v.extra.project}{v.extra.pl ? ` · PL ${v.extra.pl}` : ''}
          </Typography>
        )}
        <Doorverwijzing item={item} />

        {Array.isArray(kandidaten) && kandidaten.length > 0 && !geenConcept && (
          <Box>
            <Label>Welke mail is het origineel?</Label>
            <RadioGroup value={origineel} onChange={(e) => setOrigineel(e.target.value)}>
              {kandidaten.map((k) => (
                <FormControlLabel key={k.id} value={k.id} control={<Radio size="small" />}
                  label={(
                    <Box>
                      <Typography sx={{ fontSize: 14 }}>{k.van} · {k.onderwerp} · {(k.ontvangen || '').slice(0, 10)}</Typography>
                      {ontvangersTekst(k.ontvangers) && <Typography sx={{ fontSize: 12.5, color: KLEUR.zacht }}>Concept gaat naar: {ontvangersTekst(k.ontvangers)}</Typography>}
                    </Box>
                  )} />
              ))}
            </RadioGroup>
          </Box>
        )}

        {!geenConcept && (
          <Stack spacing={0.5}>
            <Label>Conceptantwoord</Label>
            <TextField value={concept} onChange={(e) => setConcept(e.target.value)} multiline minRows={3} fullWidth size="small"
              inputProps={{ 'aria-label': 'Conceptantwoord' }} />
            {ontvangersTekst(item.ontvangers) && <Typography sx={{ fontSize: 12.5, color: KLEUR.zacht }}>{ontvangersTekst(item.ontvangers)}</Typography>}
          </Stack>
        )}
        {!geenTaak && (
          <TextField label="Taak in je To Do" value={titel} onChange={(e) => setTitel(e.target.value)} size="small" fullWidth />
        )}

        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="contained" disableElevation disabled={bezig || modus === 'off' || !kanBeslissen || moetKiezen || (geenTaak && geenConcept)}
            onClick={() => goedkeuren(false)} sx={{ textTransform: 'none', bgcolor: KLEUR.accent }}>
            Goedkeuren
          </Button>
          {onzeker && item.status === 'vraag' && (
            <Button variant="outlined" disabled={bezig} onClick={() => goedkeuren(true)} sx={{ textTransform: 'none' }}>
              Toch opnieuw
            </Button>
          )}
          <Button variant="text" disabled={bezig || modus === 'off'} onClick={afwijzen} sx={{ textTransform: 'none', color: KLEUR.zacht }}>
            Afwijzen
          </Button>
          <Button variant="text" disabled={bezig || modus === 'off' || !kanZelfAfhandelen(item)} onClick={zelfAfgehandeld}
            sx={{ textTransform: 'none', color: KLEUR.zacht }}>
            Zelf afgehandeld
          </Button>
          <FormControlLabel control={<Checkbox size="small" checked={geenConcept} onChange={(e) => setGeenConcept(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>Geen concept</Typography>} />
          <FormControlLabel control={<Checkbox size="small" checked={geenTaak} onChange={(e) => setGeenTaak(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>Geen taak</Typography>} />
          {grippSchrijft(item) && (
            <FormControlLabel control={<Checkbox size="small" checked={geenGripp} onChange={(e) => setGeenGripp(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>Niet in Gripp</Typography>} />
          )}
          {bezig && <CircularProgress size={18} />}
        </Stack>
        <Typography sx={{ fontSize: 12, color: KLEUR.zacht }}>
          Goedkeuren = {watGebeurt(item, { geenConcept, geenTaak, geenGripp })}. Er wordt nooit iets verstuurd.
        </Typography>
        {item.fout && <Typography sx={{ fontSize: 12, color: KLEUR.fout }}>{item.fout}</Typography>}
      </Stack>
    </Box>
  );
}

function KlaarRij({ item, onKlaar }) {
  const heropen = async () => {
    try { await aiMailApi.heropen(item.id); onKlaar(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Stack direction="row" spacing={2} alignItems="baseline" sx={{ py: 1.25, borderBottom: `1px solid ${KLEUR.lijn}`, flexWrap: 'wrap' }}>
      <Label kleur={item.status === 'afgewezen' ? KLEUR.zacht : KLEUR.accent}>{STATUS_TEKST[item.status]}</Label>
      <Typography sx={{ fontSize: 14, color: KLEUR.tekst }}>{item.van_naam || item.van_email}</Typography>
      <Typography sx={{ fontSize: 14, color: KLEUR.zacht, flex: 1, minWidth: 200 }}>{item.onderwerp}</Typography>
      <Label>{item.soort_label}{item.aangepast ? ' · aangepast' : ''}{item.samengevoegd_met ? ` · bij #${item.samengevoegd_met}` : ''}</Label>
      {item.afsluit_reden && <Typography sx={{ fontSize: 12.5, color: KLEUR.zacht, flexBasis: '100%' }}>{item.afsluit_reden}</Typography>}
      {item.status !== 'samengevoegd' && <Button size="small" variant="text" onClick={heropen} sx={{ textTransform: 'none', color: KLEUR.zacht }}>Opnieuw</Button>}
    </Stack>
  );
}

function Leren({ stats }) {
  return (
    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 14, '& td, & th': { py: 1, pr: 3, textAlign: 'left', borderBottom: `1px solid ${KLEUR.lijn}` }, '& th': { fontWeight: 600, color: KLEUR.zacht, fontSize: 12.5 } }}>
      <thead><tr><th>Soort</th><th>Uitgevoerd</th><th>Ongewijzigd</th><th>Afgewezen</th><th>Open</th></tr></thead>
      <tbody>
        {stats.map((s) => (
          <tr key={s.soort}>
            <td>{s.label}</td><td>{s.uitgevoerd}</td>
            <td>{s.ongewijzigd_pct === null ? '—' : `${s.ongewijzigd_pct}%`}</td>
            <td>{s.afgewezen}</td><td>{s.open}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function MeldingSchakelaar({ melding, onGewijzigd }) {
  const [bezig, setBezig] = useState(false);
  if (!melding) return null;
  const zet = async (aan) => {
    setBezig(true);
    try {
      await aiMailApi.melding(aan);
      onGewijzigd();
      biedOngedaanAan(aan ? 'Mail bij nieuw voorstel aan' : 'Mail bij nieuw voorstel uit', async () => {
        await aiMailApi.melding(!aan); onGewijzigd();
      });
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };
  const uitleg = meldingTekst(melding);
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
      <FormControlLabel sx={{ mr: 0 }}
        control={<Switch size="small" checked={!!melding.aan} disabled={bezig} onChange={(e) => zet(e.target.checked)} />}
        label={<Typography sx={{ fontSize: 13, color: KLEUR.tekst }}>Mail mij bij een nieuw voorstel</Typography>} />
      {uitleg && <Typography sx={{ fontSize: 12, color: KLEUR.zacht }}>{uitleg}</Typography>}
    </Stack>
  );
}

export default function AiMail() {
  const [params, setParams] = useSearchParams();
  const uitMail = mailActie(params);
  const [tab, setTab] = useState('open');
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState('');
  const [ophalen, setOphalen] = useState(false);

  useEffect(() => registreerOngedaanSneltoets(), []);

  const laad = useCallback(async () => {
    setFout('');
    try {
      const st = await aiMailApi.status();
      setStatus(st);
      if (!st.mailbox_aan) { setItems([]); return; }
      if (tab === 'leren') setStats((await aiMailApi.stats()).soorten || []);
      else setItems((await aiMailApi.items(tab, tab === 'open' ? uitMail.id : null)).items || []);
    } catch (e) { setFout(e.message); } finally { setLaden(false); }
  }, [tab, uitMail.id]);

  useEffect(() => { setLaden(true); laad(); }, [laad]);

  const nuOphalen = async () => {
    setOphalen(true);
    try { await aiMailApi.ronde(); await laad(); } catch (e) { toast.error(e.message); } finally { setOphalen(false); }
  };

  const modus = status?.modus || 'off';
  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 } }}>
      <PageHeader
        icon="mark_email_read"
        title="AI-mail"
        subtitle="Mails die jij in Outlook als AI markeert of met AI: doorstuurt — voorstel, concept, één klik"
        rightActions={status?.mailbox_aan ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Label kleur={modus === 'on' ? KLEUR.accent : KLEUR.zacht}>{MODUS_TEKST[modus]}</Label>
            <Button size="small" variant="outlined" disabled={ophalen || modus === 'off'} onClick={nuOphalen} sx={{ textTransform: 'none' }}>
              {ophalen ? 'Ophalen…' : 'Nu ophalen'}
            </Button>
          </Stack>
        ) : null}
      />

      {status && !status.mailbox_aan && (
        <Typography sx={{ color: KLEUR.zacht }}>AI-mail staat (nog) niet aan voor jouw mailbox.</Typography>
      )}
      {status?.mailbox_aan && (
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>{MODUS_UITLEG[modus]}</Typography>
          <MeldingSchakelaar melding={status.melding} onGewijzigd={laad} />
          <Tabs value={tab} onChange={(_, t) => setTab(t)} sx={{ borderBottom: `1px solid ${KLEUR.lijn}`, minHeight: 40, '& .MuiTab-root': { textTransform: 'none', minHeight: 40 } }}>
            <Tab value="open" label="Te beoordelen" />
            <Tab value="klaar" label="Afgehandeld" />
            <Tab value="leren" label="Leren" />
          </Tabs>
          {laden && <CircularProgress size={22} />}
          {fout && <Typography sx={{ color: KLEUR.fout }}>{fout}</Typography>}
          {!laden && !fout && tab === 'open' && items.length === 0 && (
            <Typography sx={{ color: KLEUR.zacht, py: 3 }}>
              Niets te beoordelen. Zet in Outlook de categorie AI op een mail, of stuur hem door naar jezelf met AI: bovenaan.
            </Typography>
          )}
          {!laden && tab === 'open' && uitMail.id && !items.some((it) => it.id === uitMail.id) && (
            <Typography sx={{ fontSize: 13, color: KLEUR.zacht }}>De mail uit je melding staat niet meer open; kijk bij Afgehandeld.</Typography>
          )}
          {!laden && tab === 'open' && items.map((it) => (
            <ItemKaart key={`${it.id}-${it.status}-${it.concept_status}-${it.todo_status}`} item={it} modus={modus} onKlaar={laad}
              focus={it.id === uitMail.id} actie={it.id === uitMail.id ? uitMail.actie : null}
              onActieGezien={() => setParams({ id: String(it.id) }, { replace: true })} />
          ))}
          {!laden && tab === 'klaar' && items.map((it) => <KlaarRij key={it.id} item={it} onKlaar={laad} />)}
          {!laden && tab === 'leren' && <Leren stats={stats} />}
        </Stack>
      )}
    </Box>
  );
}
